// import { OnboardModel } from '../onboard-model';
import { SearchModel } from '../search';
import { SellModel } from '../sell-model';
import { TradeSidebar } from '../trade-sidebar';
import { UserSignInModel } from '../user-signin-model';
import { UserWalletModel } from '../user-wallet-model';
import { COMMENTS_ENABLED } from '@/client/confs/app';
import { useTradeLoginResume } from '@/client/hooks/use-trade-login-resume';
// import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'sonner';

// Lazy: DepositModel drags qr-code-styling (~47KB) and RepliesSidebar drags
// the rich editor + react-dropzone (~60KB); neither is needed for first paint.
// Deliberately NOT React.lazy: it caches a failed chunk load forever, so a
// network blip would brick the modal until reload (and crash to the app-level
// error boundary). This loads on first open, keeps the component mounted
// afterwards (close animations intact), and on failure toasts + closes so the
// next click simply retries.
const useModalChunk = <C extends ComponentType<any>>(
  open: boolean,
  setOpen: (v: boolean) => void,
  loader: () => Promise<C>,
  failText: string
) => {
  const [Comp, setComp] = useState<C | null>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!open || Comp || inFlight.current) return;
    inFlight.current = true;
    loader()
      .then(c => setComp(() => c))
      .catch(err => {
        console.error('modal chunk load failed:', err);
        toast.error(failText);
        setOpen(false);
        inFlight.current = false; // allow retry on next open
      });
  }, [open, Comp]);
  return Comp;
};

// Owns the replies state, so AppModels can drop it whole. With comments off
// (BON-248) the slot never renders: the chunk is never fetched, and a build
// with VITE_COMMENTS_ENABLED=false drops the dynamic import as dead code.
const RepliesSidebarSlot = ({ failText }: { failText: string }) => {
  const repliesOpen = useModelStore(state => state.repliesOpen);
  const setRepliesOpen = useModelStore(state => state.setRepliesOpen);
  const RepliesSidebar = useModalChunk(
    repliesOpen,
    setRepliesOpen,
    () => import('../replies-sidebar').then(m => m.RepliesSidebar),
    failText
  );
  return RepliesSidebar ? <RepliesSidebar open={repliesOpen} setOpen={setRepliesOpen} /> : null;
};

export const AppModels = () => {
  useTradeLoginResume();
  const tradeOpen = useModelStore(state => state.tradeOpen);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  const authOpen = useModelStore(state => state.authOpen);
  const setAuthOpen = useModelStore(state => state.setAuthOpen);
  const sellOpen = useModelStore(state => state.sellOpen);
  const setSellOpen = useModelStore(state => state.setSellOpen);
  const sellParams = useModelStore(state => state.sellParams);
  const searchOpen = useModelStore(state => state.searchOpen);
  const setSearchOpen = useModelStore(state => state.setSearchOpen);
  // const onboardOpen = useAppSettingStore(state => state.onboardOpen);
  // const setOnboardOpen = useAppSettingStore(state => state.setOnboardOpen);
  const walletOpen = useModelStore(state => state.walletOpen);
  const setWalletOpen = useModelStore(state => state.setWalletOpen);
  const depositOpen = useModelStore(state => state.depositOpen);
  const setDepositOpen = useModelStore(state => state.setDepositOpen);
  const { t } = useTranslation();

  const chunkFailText = t('modal-load-failed', '載入失敗,請再試一次');
  const DepositModel = useModalChunk(
    depositOpen,
    setDepositOpen,
    () => import('../deposit-model').then(m => m.DepositModel),
    chunkFailText
  );

  return (
    <>
      {/* <OnboardModel open={onboardOpen} setOpen={setOnboardOpen} /> */}
      <TradeSidebar open={tradeOpen} setOpen={setTradeOpen} />
      <UserSignInModel open={authOpen} setOpen={setAuthOpen} />
      <SellModel
        open={sellOpen}
        setOpen={setSellOpen}
        marketAddress={sellParams.marketAddress}
        optionAddress={sellParams.optionAddress}
      />
      <SearchModel open={searchOpen} setOpen={setSearchOpen} />
      {COMMENTS_ENABLED && <RepliesSidebarSlot failText={chunkFailText} />}
      <UserWalletModel open={walletOpen} setOpen={setWalletOpen} />
      {DepositModel && <DepositModel open={depositOpen} setOpen={setDepositOpen} />}
      <Toaster
        position="top-center"
        richColors={true}
        closeButton={true}
        duration={2e3}
        toastOptions={{
          classNames: { closeButton: '!border-none' },
        }}
      />
    </>
  );
};
